import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ArrowRight, Check, Star } from "lucide-react";
import { Reveal } from "../primitives/Reveal";
import { AccentTitle } from "../primitives/AccentTitle";

const EASE = [0.16, 1, 0.3, 1];
const PLAN_KEYS = ["starter", "growth", "scale"];

export const Pricing = () => {
    const { t } = useTranslation();
    const [annual, setAnnual] = useState(true);

    return (
        <section id="pricing" className="scroll-mt-24 bg-paper-grain py-24 lg:py-32" data-testid="pricing-section">
            <div className="mx-auto max-w-[1320px] px-5 lg:px-8">
                <Reveal>
                    <div className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-end">
                        <div className="max-w-2xl">
                            <p className="eyebrow">{t("home.pricing.kicker")}</p>
                            <AccentTitle
                                component="h2"
                                className="section-headline mt-5"
                                title={t("home.pricing.title")}
                                accent={t("home.pricing.titleAccent")}
                                accentColor="#8A1E12"
                            />
                            <p className="body-prose mt-6 max-w-2xl">{t("home.pricing.subtitle")}</p>
                        </div>
                        <div role="tablist" className="inline-flex items-center gap-1 rounded-[10px] border-2 border-ink-800 bg-white p-1.5 text-[15px]" data-testid="pricing-billing-toggle">
                            <button role="tab" aria-selected={!annual} onClick={() => setAnnual(false)} data-testid="pricing-monthly" className={`min-h-[48px] rounded-[8px] px-5 font-bold transition-colors duration-200 ease-nour ${!annual ? "bg-ink-800 text-white" : "text-ink-700 hover:bg-mudbrick-200"}`}>{t("home.pricing.billingMonthly")}</button>
                            <button role="tab" aria-selected={annual} onClick={() => setAnnual(true)} data-testid="pricing-annual" className={`inline-flex min-h-[48px] items-center gap-2 rounded-[8px] px-5 font-bold transition-colors duration-200 ease-nour ${annual ? "bg-ink-800 text-white" : "text-ink-700 hover:bg-mudbrick-200"}`}>
                                {t("home.pricing.billingAnnual")}
                                <span className={`inline-flex items-center rounded-[4px] px-2 py-0.5 font-mono text-[11px] font-bold tracking-wider tabular-nums num-ltr ${annual ? "bg-souq-400 text-ink-900" : "bg-souq-100 text-souq-700"}`}>{t("home.pricing.save")}</span>
                            </button>
                        </div>
                    </div>
                </Reveal>

                {/* Horizontal pricing rows — easier to compare for elderly */}
                <div className="mt-12 space-y-5" data-testid="pricing-rows">
                    {PLAN_KEYS.map((pk, i) => {
                        const features = t(`home.pricing.plans.${pk}.features`, { returnObjects: true }) || [];
                        const price = annual ? t(`home.pricing.plans.${pk}.priceAnnual`) : t(`home.pricing.plans.${pk}.priceMonthly`);
                        const suffix = annual ? t("home.pricing.perYear") : t("home.pricing.perMonth");
                        const isPopular = pk === "growth";
                        return (
                            <motion.div
                                key={pk}
                                initial={{ opacity: 0, y: 18 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.2 }}
                                transition={{ duration: 0.55, ease: EASE, delay: i * 0.08 }}
                                className={`relative grid grid-cols-1 lg:grid-cols-12 items-center gap-6 lg:gap-8 rounded-card p-7 lg:p-9 transition-all duration-300 ${isPopular
                                    ? "bg-mudbrick-200 border-4 border-madder-500 shadow-pop"
                                    : "bg-white border-2 border-ink-200 hover:border-ink-800"}`}
                                data-testid={`plan-${pk}`}
                            >
                                {isPopular && (
                                    <span className="absolute -top-4 start-7 inline-flex items-center gap-1 rounded-[6px] bg-madder-500 px-4 py-1.5 text-[12px] font-bold uppercase tracking-[0.14em] text-white" data-testid="plan-growth-popular-badge">
                                        <Star className="h-3.5 w-3.5 fill-current" />
                                        {t("home.pricing.mostPopular")}
                                    </span>
                                )}

                                {/* LEFT: name + price */}
                                <div className="lg:col-span-3">
                                    <h3 className="font-display text-[28px] font-bold tracking-tight text-ink-800">
                                        {t(`home.pricing.plans.${pk}.name`)}
                                    </h3>
                                    <p className="mt-1 text-[15px] font-semibold text-ink-600">
                                        {t(`home.pricing.plans.${pk}.tag`)}
                                    </p>
                                    <div className="mt-5 flex items-baseline gap-2">
                                        {Number(price) === 0 ? (
                                            <span className="font-display text-[56px] font-extrabold leading-none tracking-tight text-ink-800">Free</span>
                                        ) : (
                                            <>
                                                <span className="font-display text-[56px] font-extrabold leading-none tracking-tight tabular-nums num-ltr text-ink-800">{price}</span>
                                                <span className="text-[16px] font-bold num-ltr tabular-nums text-ink-700">SAR{suffix}</span>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* MIDDLE: features grid */}
                                <ul className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3 text-[16px]">
                                    {features.map((f) => (
                                        <li key={f} className="flex items-start gap-2.5 font-semibold text-ink-800">
                                            <span className={`mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-[6px] ${isPopular ? "bg-madder-500 text-white" : "bg-redsea-500 text-white"}`}>
                                                <Check className="h-3.5 w-3.5" strokeWidth={3.5} />
                                            </span>
                                            {f}
                                        </li>
                                    ))}
                                </ul>

                                {/* RIGHT: CTA */}
                                <div className="lg:col-span-2 flex lg:justify-end">
                                    <button
                                        type="button"
                                        data-testid={`plan-${pk}-choose`}
                                        className={`inline-flex w-full lg:w-auto min-h-[56px] items-center justify-center gap-2 rounded-btn px-6 text-[16px] font-bold transition-all duration-200 ease-nour ${isPopular
                                            ? "bg-madder-500 text-white hover:bg-madder-600 active:translate-y-[1px]"
                                            : "border-2 border-ink-800 bg-white text-ink-800 hover:bg-ink-800 hover:text-white"}`}
                                    >
                                        {t("home.pricing.choose")}
                                        <ArrowRight className="h-5 w-5 rtl-mirror" />
                                    </button>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};
